
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
	MaterialReactTable,
	useMaterialReactTable,
} from "material-react-table";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { Button, Icon, ICONS } from "abs-ui-components";

import "../VesselDetails.scss";

import { myFleetActions } from "../../../store/myFleet/myFleet.slice";
import axiosHttp from "../../../services/interceptor";
import { preventScientificNotation } from "../../../services/utilities";

const otherFuelsStaticData = [
	{
		FuelRegistryDtlId: 10,
		VesselMstId: null,
		FuelType: "OF1",
		FuelName: "Other Fuel 1",
		Lcv: 0,
		Co2eq: 0,
		Cf: 0,
		EfCo2: 0,
		EfCh4: 0,
		EfN2o: 0,
		Rwd: 0,
		Cslip: true,
		IsEthanol: false,
		IsEditable: true,
	},
	{
		FuelRegistryDtlId: 11,
		VesselMstId: null,
		FuelType: "OF2",
		FuelName: "Other Fuel 2",
		Lcv: 0,
		Co2eq: 0,
		Cf: 0,
		EfCo2: 0,
		EfCh4: 0,
		EfN2o: 0,
		Rwd: 0,
		Cslip: true,
		IsEthanol: false,
		IsEditable: true,
	},
	{
		FuelRegistryDtlId: 12,
		VesselMstId: null,
		FuelType: "OF3",
		FuelName: "Other Fuel 3",
		Lcv: 0,
		Co2eq: 0,
		Cf: 0,
		EfCo2: 0,
		EfCh4: 0,
		EfN2o: 0,
		Rwd: 0,
		Cslip: true,
		IsEthanol: false,
		IsEditable: true,
	},
];

const FuelRegistryFormGroup = (props) => {
	const [validationErrors, setValidationErrors] = useState({});
	const dispatch = useDispatch();
	const [fuelData, setRows] = useState([]);
	const [isDataModified, setIsDataModified] = useState(false);
	const [modifiedFuels, setModifiedFuels] = useState([]);

	const validateRequired = (value) => {
		if (typeof value === "string") {
			return !!value.length;
		} else if (typeof value === "number") {
			return value >= 0;
		}
		return false;
	};

	const fetchDefaultFuels = async () => {
		try {
			const response = await axiosHttp.get(
				"api/MyFleet/GetConstantFuelRegistry"
			);
			const fetchedData = response?.data?.Data;
			const mergedData = [...fetchedData, ...otherFuelsStaticData];
			const processedData = mergedData?.map((item) => ({
				...item,
				Cslip: item?.Cslip ? "Applicable" : "Not Applicable",
			}));
			if (
				!props?.vesselMstId ||
				!props.vesselData?.FuelRegistryDtls?.length
			) {
				setRows(processedData);
			}
		} catch (error) {
			console.error("Failed to fetch data:", error);
		}
	};
	useEffect(() => {
		setModifiedFuels([]);
	}, []);

	useEffect(() => {
		fetchDefaultFuels();
	}, []);

	useEffect(() => {
		if (!isDataModified && !props?.vesselMstId) {
			setModifiedFuels((prevModifiedFuels) => [
				...prevModifiedFuels,
				...otherFuelsStaticData,
			]);
			dispatch(
				myFleetActions.saveFuelRegistryPayload({
					fuelRegistryPayload: JSON.stringify(
						otherFuelsStaticData || []
					),
				})
			);
		}
	}, []);

	useEffect(() => {
		if (
			props?.vesselMstId &&
			Array.isArray(props?.vesselData?.FuelRegistryDtls)
		) {
			const fuelUpdatedValues = props.vesselData.FuelRegistryDtls.map(
				(item) => ({
					...item,
					Cslip: item.Cslip ? "Applicable" : "Not Applicable",
				})
			);
			const sortedUpdatedFuels = sortByOrder(
				fuelUpdatedValues,
				"FuelType"
			);
			const mergedData = sortedUpdatedFuels?.map((item, index) => ({
				...item,
				IsEditable: index >= 9,
			}));
			setRows(mergedData);

			dispatch(
				myFleetActions.saveFuelRegistryPayload({
					fuelRegistryPayload: JSON.stringify(mergedData),
				})
			);
		}
	}, [props?.vesselMstId, props?.vesselData?.FuelRegistryDtls]);

	const orderArray = [
		"MDO",
		"MGO",
		"LFO",
		"HFO",
		"LPGP",
		"LPGB",
		"LNG",
		"METH",
		"ETHA",
		"OF1",
		"OF2",
		"OF3",
	];

	const sortByOrder = (arrayToSort, key) => {
		return arrayToSort.sort((a, b) => {
			const indexA = orderArray?.indexOf(a[key]);
			const indexB = orderArray?.indexOf(b[key]);

			// Handle cases where the value is not in the orderArray
			const adjustedIndexA =
				indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
			const adjustedIndexB =
				indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;

			return adjustedIndexA - adjustedIndexB;
		});
	};

	const RowActions = {
		Edit: 0,
		Add: 1,
	};

	// let activeAction = -1;
	const [activeAction, setactiveAction] = useState(-1);

	const handleFuelActions = async (table, row, action) => {
		if (activeAction === -1) {
			if (action === RowActions.Edit) {
				table.setEditingRow(row);
				setactiveAction(action);
			} else if (action === RowActions.Add) {
				table.setCreatingRow(true);
				setactiveAction(action);
			}
			return;
		} else return;
	};

	const resetActiveRow = () => {
		setactiveAction(-1);
		setValidationErrors({});
	};

	const handleCreateUser = async ({ values, table }) => {
		const newValidationErrors = validateUser(values, "add", {});
		if (Object.values(newValidationErrors).some((error) => error)) {
			setValidationErrors(newValidationErrors);
			return;
		}
		setValidationErrors({});
		table.setCreatingRow(null);

		const updatedValues = {
			...values,
			IsEditable: true,
			IsDeleteEnabled: true,
			IsFuelCodeEditable: true,
		};

		const updatedData = [...fuelData, updatedValues];
		const finalMappedData = updatedData.map((fuel, index) => ({
			...fuel,
			FuelRegistryDtlId: index + 1,
			VesselMstId: props.vesselMstId || 0,
		}));

		if (!props.vesselMstId) {
			setModifiedFuels((prevModifiedFuels) => [
				...prevModifiedFuels,
				updatedValues,
			]);
			const editedFuels = [...modifiedFuels, updatedValues];
			setIsDataModified(true);
			setRows(finalMappedData);
			dispatch(
				myFleetActions.saveFuelRegistryPayload({
					fuelRegistryPayload: JSON.stringify(editedFuels),
				})
			);
		} else {
			setModifiedFuels((prevModifiedFuels) => [
				...prevModifiedFuels,
				updatedValues,
			]);
			setRows(finalMappedData);
			const sortedUpdatedFuels = sortByOrder(finalMappedData, "FuelType");
			dispatch(
				myFleetActions.saveFuelRegistryPayload({
					fuelRegistryPayload: JSON.stringify(sortedUpdatedFuels),
				})
			);
		}
		resetActiveRow();
	};
	const handleSaveUser = async (editedObject) => {
		const { row, values } = editedObject;
		const newValidationErrors = validateUser(values, "edit", row);
		if (Object.values(newValidationErrors).some((error) => error)) {
			setValidationErrors(newValidationErrors);
			return;
		}
		setValidationErrors({});
		table.setEditingRow(null);
		
		const fuelIndex = fuelData.findIndex(
			(fuel) => fuel.FuelRegId === row.id
		);

		let updatedData;
		if (fuelIndex !== -1) {
			updatedData = fuelData.map((fuel, index) =>
				index === fuelIndex ? { ...fuel, ...values } : fuel
			);

			//enrooll vessel
			if (!props.vesselMstId) {
				let editedMergedFuels;
				const modifiedFuelIndex = modifiedFuels.findIndex(
					(fuel) => fuel.FuelType === values.FuelType
				);
				if (modifiedFuelIndex !== -1) {
					const updatedModifiedData = modifiedFuels.map(
						(fuel, index) =>
							index === modifiedFuelIndex
								? { ...fuel, ...values }
								: fuel
					);
					editedMergedFuels = [...updatedModifiedData];
					setModifiedFuels(updatedModifiedData);
				} else {
					editedMergedFuels = [...modifiedFuels, values];
					setModifiedFuels(editedMergedFuels);
				}

				setIsDataModified(true);
				setRows(updatedData);
				dispatch(
					myFleetActions.saveFuelRegistryPayload({
						fuelRegistryPayload: JSON.stringify(editedMergedFuels),
					})
				);
			} else {
				//edit vessel case
				setRows(updatedData);
				const sortedUpdatedFuels = sortByOrder(updatedData, "FuelType");
				dispatch(
					myFleetActions.saveFuelRegistryPayload({
						fuelRegistryPayload: JSON.stringify(sortedUpdatedFuels),
					})
				);
			}
		} else {
			if (!props.vesselMstId) {
				setModifiedFuels((prevModifiedFuels) => [
					...prevModifiedFuels,
					values,
				]);
				const editedFuels = modifiedFuels.map((fuel) =>
					fuel.FuelType === values.FuelType ? values : fuel
				);
				setModifiedFuels(editedFuels);
				const hasEthaFuel = editedFuels.some(
					(fuel) => fuel.FuelType === "ETHA"
				);
				if (hasEthaFuel) {
					const updatedData = [
						...fuelData?.slice(0, 8),
						...editedFuels.map((item) => ({
							...item,
							IsEditable: true,
							IsDeleteEnabled:
								item?.FuelType === "ETHA" ? false : true,
						})),
					];
					setIsDataModified(true);
					setRows(updatedData);
					dispatch(
						myFleetActions.saveFuelRegistryPayload({
							fuelRegistryPayload: JSON.stringify(editedFuels),
						})
					);
				} else {
					const updatedData = [
						...fuelData?.slice(0, 9),
						...editedFuels.map((item) => ({
							...item,
							IsEditable: true,
							IsDeleteEnabled: true,
						})),
					];
					setIsDataModified(true);
					setRows(updatedData);
					dispatch(
						myFleetActions.saveFuelRegistryPayload({
							fuelRegistryPayload: JSON.stringify(editedFuels),
						})
					);
				}
			} else {
			}
		}
		resetActiveRow();
	};

	const handleDeleteFuel = (index) => {
		const hasEthaFuel = modifiedFuels.some(
			(fuel) => fuel.FuelType === "ETHA"
		);

		let updatedFuelData;

		if (!props?.vesselMstId) {
			if (hasEthaFuel) {
				const fuelToDelete = fuelData[index];
				updatedFuelData = fuelData.filter((_, i) => i !== index);
				const indexToDelete = modifiedFuels.findIndex(
					(fuel) => fuel.FuelType === fuelToDelete.FuelType
				);
				const updatedModifiedFuels = modifiedFuels.filter(
					(_, i) => i !== indexToDelete
				);
				setRows(updatedFuelData);
				setModifiedFuels(updatedModifiedFuels);
				dispatch(
					myFleetActions.saveFuelRegistryPayload({
						fuelRegistryPayload: JSON.stringify(
							updatedFuelData.slice(8)
						),
					})
				);
			} else {
				const fuelToDelete = fuelData[index];
				const indexToDelete = modifiedFuels.findIndex(
					(fuel) => fuel.FuelType === fuelToDelete.FuelType
				);
				const updatedModifiedFuels = modifiedFuels.filter(
					(_, i) => i !== indexToDelete
				);
				updatedFuelData = fuelData.filter((_, i) => i !== index);
				setRows(updatedFuelData);
				setModifiedFuels(updatedModifiedFuels);

				dispatch(
					myFleetActions.saveFuelRegistryPayload({
						fuelRegistryPayload: JSON.stringify(
							updatedFuelData.slice(9)
						),
					})
				);
			}
		} else {
			updatedFuelData = fuelData.filter((_, i) => i !== index);
			setRows(updatedFuelData);

			const sortedUpdatedFuels = sortByOrder(updatedFuelData, "FuelType");
			dispatch(
				myFleetActions.saveFuelRegistryPayload({
					fuelRegistryPayload: JSON.stringify(sortedUpdatedFuels),
				})
			);
		}
	};

	function validateUser(row, type, rowObject) {
		const otherRows = fuelData.filter(
			(fuel) => fuel.FuelType !== rowObject?.original?.FuelType
		);
		return {
			FuelType: !validateRequired(row.FuelType)
				? "Fuel Code is Required"
				: (
					type === "add"
						? fuelData.some(
							(fuel) => fuel.FuelType === row.FuelType
						)
						: otherRows.some(
							(fuel) => fuel.FuelType === row.FuelType
						)
				)
					? "Fuel Type already exists"
					: "",
			FuelName: !validateRequired(row.FuelName)
				? "Fuel Name is Required"
				: "",
			Lcv: !validateRequired(row.Lcv) ? "LCV is Required" : "",
			Co2eq: !validateRequired(row.Co2eq) ? "CO2WtT is Required" : "",
			Cf: !validateRequired(row.Cf) ? "Cf is Required" : "",
			EfCo2: !validateRequired(row.EfCo2) ? "EFCO2is Required" : "",
			EfCh4: !validateRequired(row.EfCh4) ? "EFCH4 is Required" : "",
			EfN2o: !validateRequired(row.EfN2o) ? "EFN2O is Required" : "",
			Rwd: !validateRequired(row.Rwd) ? "RWD is Required" : "",
			Cslip: !validateRequired(row.Cslip) ? "Cslip is Required" : "",
		};
	}

	const handleDisableCell = (fuelType) => {
		return fuelType === "ETHA" ||
			fuelType === "MDO" ||
			fuelType === "MGO" ||
			fuelType === "LFO" ||
			fuelType === "HFO" ||
			fuelType === "LPGP" ||
			fuelType === "LPGB" ||
			fuelType === "LNG" ||
			fuelType === "METH"
			? "#c3bebe"
			: "";
	};

	const alphanumericAndLengthValidation = (value) => {
		const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, "");
		return alphanumericValue.length <= 10
			? alphanumericValue
			: alphanumericValue.slice(0, 10);
	};

	const alphaNumericValidation = (value) => {
		const truncatedValue =
			value.length <= 100 ? value : value.slice(0, 100);
		return truncatedValue;
	};

	const columns = useMemo(
		() => [
			{
				accessorKey: "FuelType",
				header: "Fuel Code",
				muiTableBodyCellProps: ({ row }) => ({
					sx: {
						color:
							row?.original?.IsEditable &&
								row?.original?.IsDeleteEnabled
								? ""
								: "#c3bebe",
					},
				}),
				muiEditTextFieldProps: ({ row }) => ({
					required: true,
					error: !!validationErrors?.FuelType,
					helperText: validationErrors?.FuelType,
					disabled:
						row.original.IsEthanol ||
						(row?.original?.IsEditable &&
							!row?.original?.IsFuelCodeEditable),
					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							FuelType: undefined,
						}),
					onChange: (e) => {
						const validatedValue = alphanumericAndLengthValidation(
							e.target.value
						).toUpperCase();
						e.target.value = validatedValue;
					},
				}),
			},
			{
				accessorKey: "FuelName",
				header: "Fuel Name",
				muiTableBodyCellProps: ({ row }) => ({
					sx: {
						color:
							!props.isEnrollVessel &&
								props.vesselMstId &&
								(props?.regulations?.trim() === "EUMRV" ||
									"UKMRV" ||
									"DCS")
								? "#c3bebe"
								: handleDisableCell(row.original.FuelType),
					},
				}),
				muiEditTextFieldProps: ({ row }) => ({
					required: true,
					error: !!validationErrors?.FuelName,
					helperText: validationErrors?.FuelName,
					disabled: row.original.IsEthanol,
					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							FuelName: undefined,
						}),
					onChange: (e) => {
						const validatedValue = alphaNumericValidation(
							e.target.value
						);
						e.target.value = validatedValue;
					},
				}),
			},
			{
				accessorKey: "Lcv",
				header: "LCV (MJ/g)",
				muiTableBodyCellProps: ({ row }) => ({
					sx: {
						color:
							!props.isEnrollVessel &&
								props.vesselMstId &&
								(props?.regulations?.trim() === "EUMRV" ||
									"UKMRV" ||
									"DCS")
								? "#c3bebe"
								: row.original.FuelType === "MDO" ||
									row.original.FuelType === "MGO" ||
									row.original.FuelType === "LFO" ||
									row.original.FuelType === "HFO" ||
									row.original.FuelType === "LPGP" ||
									row.original.FuelType === "LPGB" ||
									row.original.FuelType === "LNG" ||
									row.original.FuelType === "METH"
									? "#c3bebe"
									: "",
						borderRight: "1px solid lightgray",
						"&:last-child": {
							borderRight: "none",
						},
					},
				}),
				muiEditTextFieldProps: {
					required: true,
					error: !!validationErrors?.Lcv,
					helperText: validationErrors?.Lcv,
					type: "number",

					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							Lcv: undefined,
						}),
					onChange: (e) => {
						const validatedValue = e.target.value
							.replace(/[^0-9.]/g, "")
							.replace(/(\..*)\./g, "$1");
						e.target.value = validatedValue;
					},
					onKeyDown: (e) => {
						preventScientificNotation(e);
						// Prevent up and down arrow keys
						if (e.key === "ArrowUp" || e.key === "ArrowDown") {
							e.preventDefault();
						}
					},
					onWheel: (e) => e.target.blur(),
				},
			},
			{
				id: "wtt",
				header: "WtT",
				muiTableHeadCellProps: {
					className: "grouped-header",
				},
				columns: [
					{
						accessorKey: "Co2eq",
						header: "CO2 WtT (gCO2eq /MJ)",
						muiTableBodyCellProps: ({ row }) => ({
							sx: {
								color:
									!props.isEnrollVessel &&
										props.vesselMstId &&
										(props?.regulations?.trim() === "EUMRV" ||
											"UKMRV" ||
											"DCS")
										? "#c3bebe"
										: row.original.FuelType === "MDO" ||
											row.original.FuelType === "MGO" ||
											row.original.FuelType === "LFO" ||
											row.original.FuelType === "HFO" ||
											row.original.FuelType === "LPGP" ||
											row.original.FuelType === "LPGB" ||
											row.original.FuelType === "LNG" ||
											row.original.FuelType === "METH"
											? "#c3bebe"
											: "",
								borderRight: "1px solid lightgray",
								"&:last-child": {
									borderRight: "none",
								},
							},
						}),
						muiEditTextFieldProps: {
							required: true,
							error: !!validationErrors?.Co2eq,
							helperText: validationErrors?.Co2eq,
							type: "number",
							onFocus: () =>
								setValidationErrors({
									...validationErrors,
									Co2eq: undefined,
								}),
							onChange: (e) => {
								const validatedValue = e.target.value
									.replace(/[^0-9.]/g, "")
									.replace(/(\..*)\./g, "$1");

								e.target.value = validatedValue;
							},
							onKeyDown: (e) => {
								preventScientificNotation(e);
								// Prevent up and down arrow keys
								if (
									e.key === "ArrowUp" ||
									e.key === "ArrowDown"
								) {
									e.preventDefault();
								}
							},
							onWheel: (e) => e.target.blur(),
						},
					},
				],
			},
			{
				id: "ttw",
				header: "TtW",
				muiTableHeadCellProps: {
					className: "grouped-header",
				},
				columns: [
					{
						accessorKey: "Cf",
						header: "Cf",
						muiTableBodyCellProps: ({ row }) => ({
							sx: {
								color:
									!props.isEnrollVessel &&
										props.vesselMstId &&
										(props?.regulations?.trim() === "EUMRV" ||
											"UKMRV" ||
											"DCS")
										? "#c3bebe"
										: handleDisableCell(
											row.original.FuelType
										),
							},
						}),
						muiEditTextFieldProps: ({ row }) => ({
							required: true,
							error: !!validationErrors?.Cf,
							helperText: validationErrors?.Cf,
							type: "number",
							disabled: row.original.IsEthanol,

							onFocus: () =>
								setValidationErrors({
									...validationErrors,
									Cf: undefined,
								}),
							onChange: (e) => {
								const validatedValue = e.target.value
									.replace(/[^0-9.]/g, "")
									.replace(/(\..*)\./g, "$1");

								e.target.value = validatedValue;
							},
							onKeyDown: (e) => {
								preventScientificNotation(e);
								// Prevent up and down arrow keys
								if (
									e.key === "ArrowUp" ||
									e.key === "ArrowDown"
								) {
									e.preventDefault();
								}
							},
							onWheel: (e) => e.target.blur(),
						}),
					},
					{
						accessorKey: "EfCo2",
						header: "EF CO2",
						muiTableBodyCellProps: ({ row }) => ({
							sx: {
								color:
									!props.isEnrollVessel &&
										props.vesselMstId &&
										(props?.regulations?.trim() === "EUMRV" ||
											"UKMRV" ||
											"DCS")
										? "#c3bebe"
										: handleDisableCell(
											row.original.FuelType
										),
							},
						}),
						muiEditTextFieldProps: ({ row }) => ({
							required: true,
							error: !!validationErrors?.EfCo2,
							helperText: validationErrors?.EfCo2,
							type: "number",
							disabled: row.original.IsEthanol,
							onFocus: () =>
								setValidationErrors({
									...validationErrors,
									EfCo2: undefined,
								}),
							onChange: (e) => {
								const validatedValue = e.target.value
									.replace(/[^0-9.]/g, "")
									.replace(/(\..*)\./g, "$1");
								e.target.value = validatedValue;
							},
							onKeyDown: (e) => {
								preventScientificNotation(e);
								// Prevent up and down arrow keys
								if (
									e.key === "ArrowUp" ||
									e.key === "ArrowDown"
								) {
									e.preventDefault();
								}
							},
							onWheel: (e) => e.target.blur(),
						}),
					},
					{
						accessorKey: "EfCh4",
						header: "EF CH4",
						muiTableBodyCellProps: ({ row }) => ({
							sx: {
								color:
									!props.isEnrollVessel &&
										props.vesselMstId &&
										(props?.regulations?.trim() === "EUMRV" ||
											"UKMRV" ||
											"DCS")
										? "#c3bebe"
										: handleDisableCell(
											row.original.FuelType
										),
							},
						}),
						muiEditTextFieldProps: ({ row }) => ({
							required: true,
							error: !!validationErrors?.EfCh4,
							helperText: validationErrors?.EfCh4,
							type: "number",
							disabled: row.original.IsEthanol,

							onFocus: () =>
								setValidationErrors({
									...validationErrors,
									EfCh4: undefined,
								}),
							onChange: (e) => {
								const validatedValue = e.target.value
									.replace(/[^0-9.]/g, "")
									.replace(/(\..*)\./g, "$1");

								e.target.value = validatedValue;
							},
							onKeyDown: (e) => {
								preventScientificNotation(e);
								// Prevent up and down arrow keys
								if (
									e.key === "ArrowUp" ||
									e.key === "ArrowDown"
								) {
									e.preventDefault();
								}
							},
							onWheel: (e) => e.target.blur(),
						}),
					},
					{
						accessorKey: "EfN2o",
						header: "EF N2O",
						muiTableBodyCellProps: ({ row }) => ({
							sx: {
								color:
									!props.isEnrollVessel &&
										props.vesselMstId &&
										(props?.regulations?.trim() === "EUMRV" ||
											"UKMRV" ||
											"DCS")
										? "#c3bebe"
										: handleDisableCell(
											row.original.FuelType
										),
							},
						}),
						muiEditTextFieldProps: ({ row }) => ({
							required: true,
							error: !!validationErrors?.EfN2o,
							helperText: validationErrors?.EfN2o,
							type: "number",
							disabled: row.original.IsEthanol,
							onFocus: () =>
								setValidationErrors({
									...validationErrors,
									EfN2o: undefined,
								}),
							onChange: (e) => {
								const validatedValue = e.target.value
									.replace(/[^0-9.]/g, "")
									.replace(/(\..*)\./g, "$1");

								e.target.value = validatedValue;
							},
							onKeyDown: (e) => {
								preventScientificNotation(e);
								// Prevent up and down arrow keys
								if (
									e.key === "ArrowUp" ||
									e.key === "ArrowDown"
								) {
									e.preventDefault();
								}
							},
							onWheel: (e) => e.target.blur(),
						}),
					},
				],
			},
			{
				accessorKey: "Rwd",
				header: "RWD",
				size: 100,
				muiTableBodyCellProps: ({ row }) => ({
					sx: {
						color:
							!props.isEnrollVessel &&
								props.vesselMstId &&
								(props?.regulations?.trim() === "EUMRV" ||
									"UKMRV" ||
									"DCS")
								? "#c3bebe"
								: handleDisableCell(row.original.FuelType),
					},
				}),
				muiEditTextFieldProps: ({ row }) => ({
					required: true,
					error: !!validationErrors?.Rwd,
					helperText: validationErrors?.Rwd,
					type: "number",
					disabled: row.original.IsEthanol,
					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							Rwd: undefined,
						}),
					onChange: (e) => {
						const validatedValue = e.target.value
							.replace(/[^0-9.]/g, "")
							.replace(/(\..*)\./g, "$1");

						e.target.value = validatedValue;
					},
					onKeyDown: (e) => {
						preventScientificNotation(e);
						// Prevent up and down arrow keys
						if (e.key === "ArrowUp" || e.key === "ArrowDown") {
							e.preventDefault();
						}
					},
					onWheel: (e) => e.target.blur(),
				}),
			},
			{
				accessorKey: "Cslip",
				header: "Cslip",
				editVariant: "select",
				editSelectOptions: [
					{ label: "Applicable", value: "Applicable" },
					{ label: "Not Applicable", value: "Not Applicable" },
				],
				muiTableBodyCellProps: ({ row }) => ({
					sx: {
						color:
							!props.isEnrollVessel &&
								props.vesselMstId &&
								(props?.regulations?.trim() === "EUMRV" ||
									"UKMRV" ||
									"DCS")
								? "#c3bebe"
								: handleDisableCell(row.original.FuelType),
					},
				}),
				muiEditTextFieldProps: ({ row }) => ({
					required: true,
					error: !!validationErrors?.Cslip,
					helperText: validationErrors?.Cslip,
					select: true,
					disabled: row.original.IsEthanol,
					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							Cslip: undefined,
						}),
					onChange: (e) => {
						const selectedValue = e.target.value;
					},
				}),
			},
		],
		[validationErrors]
	);

	const table = useMaterialReactTable({
		columns,
		data: fuelData,
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar:
			!props.isEnrollVessel &&
				props.vesselMstId &&
				(props?.regulations?.trim() === "EUMRV" || "UKMRV" || "DCS")
				? false
				: true,
		enableBottomToolbar: false,
		enableColumnActions: false,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enableEditing:
			!props.isEnrollVessel &&
				props.vesselMstId &&
				(props?.regulations?.trim() === "EUMRV" || "UKMRV" || "DCS")
				? false
				: true,
		enablePagination: false,
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": {
					borderRight: "none",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "520px",
			},
		},

		muiTableBodyCellProps: ({ row, cell }) => {
			return {
				sx: {
					borderRight: "1px solid lightgray",
					"&:last-child": {
						borderRight: "none",
					},
					color:
						row.original.FuelType === "MDO" ||
							row.original.FuelType === "MGO" ||
							row.original.FuelType === "LFO" ||
							row.original.FuelType === "HFO" ||
							row.original.FuelType === "LPGP" ||
							row.original.FuelType === "LPGB" ||
							row.original.FuelType === "LNG" ||
							row.original.FuelType === "METH"
							? "#c3bebe"
							: "",
				},
			};
		},
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				"&:not(:last-of-type)": {
					borderBottom: "1px solid lightgray",
				},
				verticalAlign: "top",
				// backgroundColor: !row?.IsEthanol ? "rgb(224, 224, 224)" : "red",
			},
		}),
		muiTableHeadRowProps: {
			sx: {
				borderBottom: "2px solid lightgray",
			},
		},
		getRowId: (row) => row.FuelRegId,

		onCreatingRowSave: handleCreateUser,
		onEditingRowCancel: resetActiveRow,
		onCreatingRowCancel: resetActiveRow,
		onEditingRowSave: handleSaveUser,
		renderToolbarInternalActions: ({ table }) => <></>,
		renderRowActions: ({ row, table }) => {
			return (
				<Box sx={{ display: "flex", gap: "1rem" }}>
					{(row?.original?.IsEthanol ||
						row?.original?.IsEditable) && (
							<Tooltip title="Edit">
								<IconButton
									style={{ padding: "0px" }}
									onClick={() =>
										handleFuelActions(
											table,
											row,
											RowActions.Edit
										)
									}
								>
									<EditIcon />
								</IconButton>
							</Tooltip>
						)}
					{row?.original?.IsDeleteEnabled && (
						<Tooltip title="Delete">
							<IconButton
								style={{ padding: "0px" }}
								color="error"
								onClick={() => handleDeleteFuel(row?.index)}
							>
								<DeleteIcon />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			);
		},
		renderTopToolbarCustomActions: ({ table }) => (
			<div className="mt-1">
				<Button
					startIcon={<Icon icon={ICONS.ADD} />}
					onClick={() => {
						handleFuelActions(table, null, RowActions.Add);
					}}
					disabled={!!table.getState().creatingRow}
				>
					Add New Fuel
				</Button>
			</div>
		),
	});

	return (
		<div className="fuel-registry mt-5">
			<MaterialReactTable table={table} />
		</div>
	);
};

export default FuelRegistryFormGroup;
